import { useState, useEffect } from 'react';
import "./FormSettings.css";
import Modal from "../shared/Modal";
import { useDispatch, useSelector } from 'react-redux';
import { selectEditingForm, selectUIStates, setEditingForm, setUIStates } from '../../redux/features/appData';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { apiFormHasEdits, apiUpdateFormSettings } from '../../services/FormService';
import { UpdateFormSettingsPayload } from 'effno-core';
import { toastFail, toastSuccess } from '../../config';
import toast, { Toaster } from 'react-hot-toast';
import { validateEmail, validateURL } from '../shared/responseValidators';
import { Timezone, TIMEZONES } from '../config/timezones';
import { MySwitch } from '../form-widgets/MySwitch';
import { GenericSelect } from '../form-widgets/GenericSelect';
import { FormHeader } from '../form-header/FormHeader';
import { useNavigate } from 'react-router-dom';

const HOURS = Array.from(Array(24).keys());
const MINUTES = Array.from(Array(60).keys());

const SETTINGS_MENU = {
    GENERAL: "general",
    COMPLETION: "completion",
    ACCESS: "access",
    // NOTIFICATIONS: "notifications",
}

type FormSettings = {
    redirectOnCompletion: boolean;
    redirectURL: string | undefined;
    progressBar: boolean;
    questionNumbers: boolean;
    notifyCreator: boolean;
    notifyEmails: string | undefined;
    notifyResponder: boolean;
    passwordProtected: boolean;
    passwordPlain: string | undefined;
    closed: boolean;
    closeDate: Date | null;
    closeDateTimezone: number | null;
    closeDateTimezoneId?: string;
    submissionLimit: number | null;
}

type FormSettingsProps = {
}

export const FormSettings = ({}: FormSettingsProps) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const editingForm = useSelector(selectEditingForm);
    const uiStates = useSelector(selectUIStates);

    const [settings, setSettings] = useState<FormSettings | null>(null);
    const [menu, setMenu] = useState<string>(SETTINGS_MENU.GENERAL);
    const [hours, setHours] = useState<number>(0);
    const [minutes, setMinutes] = useState<number>(0);
    const [pickerDate, setPickerDate] = useState<Date | null>(new Date());
    const [showSubmissionLimit, setShowSubmissionLimit] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);


    useEffect(()=> {
        if (editingForm && editingForm.formSettings) {
            const newSettings : FormSettings = {...editingForm.formSettings}

            console.log("[FormSettings] changing to newSettings: ", newSettings);

            setShowSubmissionLimit(newSettings.submissionLimit !== null);
            setPickerDate(newSettings.closeDate ? new Date(newSettings.closeDate) : new Date());
            setHours(newSettings.closeDate ? new Date(newSettings.closeDate).getHours() : 0);
            setMinutes(newSettings.closeDate ? new Date(newSettings.closeDate).getMinutes() : 0);
            setSettings(newSettings);
        }
    }, [editingForm])

    const handleChangeSetting = async (e: any) => {
        console.log("[FormSettings/handleChangeCloseDate] target name: ", e.target.name);
        console.log("[FormSettings/handleChangeCloseDate] target value: ", e.target.value);
        console.log("[FormSettings/handleChangeCloseDate] target checked: ", e.target.checked);

        if (!settings) {
            return;
        }

        switch (e.target.name) {
            case "closed":
            case "redirectOnCompletion":
            case "notifyCreator":
            case "notifyResponder":
            case "progressBar":
            case "questionNumbers":
                setSettings({...settings, [e.target.name]: e.target.checked})
                break;
            case "closeOnDate":
                let newSettings : FormSettings = {...settings}

                if (e.target.checked && !settings.closeDate) {
                    newSettings.closeDate = new Date();
                } else if (!e.target.checked) {
                    newSettings.closeDate = null;
                }
                console.error("Updated date settings: ", newSettings)
                setSettings(newSettings)

                break;
            case "passwordProtected":
                setSettings({...settings, [e.target.name]: e.target.checked})
                break;
            case "submissionLimit":
                const limit = parseInt(e.target.value);
                setSettings({...settings, submissionLimit: limit})
                break;
            case "closeDateTimezone":
                const offset = parseInt(e.target.value);
                setSettings({...settings, closeDateTimezone: offset})
                break;
            default:
                setSettings({...settings, [e.target.name]: e.target.value})
        }

    }

    const createDateWithOffset = (
        year: number, month: number, date: number, hour: number, minute: number, gmtOffset: number
    ) => {
        // console.error("[FormSettings/createDateWithOffset] gmtoffset: ", gmtOffset);

        const localOffsetMinutes = new Date().getTimezoneOffset(); // local offset
        // console.error("[FormSettings/createDateWithOffset] localOffsetMinutes: ", localOffsetMinutes);


        // gmtOffset is in hours (e.g., -5 for GMT-5)
        const offsetInMinutes = -1 * ((gmtOffset * 60) + localOffsetMinutes);

        // console.error("[FormSettings/createDateWithOffset] offsetMinutes: ", offsetInMinutes);
        
      
        // Create a date in local time
        const localDate = new Date(year, month - 1, date, hour, minute);
        // console.error("[FormSettings/createDateWithOffset] localDate: ", localDate);
        
      
        // Adjust the date to account for the offset
        const utcTime = localDate.getTime() + offsetInMinutes * 60 * 1000;
        // console.error("[FormSettings/createDateWithOffset] utcTime: ", utcTime);        
      
        // Return the adjusted date
        return new Date(utcTime);
    }

    const handleChangeCloseDate = async (date: Date | null) => {
        console.log("[FormSettings/handleChangeCloseDate] date: ", date);

        if (!date) {
            return;
        }

        const newDate = createDateWithOffset(
            date.getFullYear(), date.getMonth() + 1, date.getDate(), 
            hours, minutes,
            settings?.closeDateTimezone || -8)

        setSettings({
            ...settings,
            closeDate: newDate,
        } as FormSettings)

        setPickerDate(date);
    }

    const handleSave = async () => {
        console.log("[handleSave] settings: ", settings)

        if (!editingForm || !settings) {
            return;
        }

        // check requirements
        if (settings?.redirectOnCompletion && settings?.redirectURL && settings.redirectURL.length > 0) {
            if (!validateURL(settings.redirectURL)) {
                setError("Invalid redirect URL");
                return;
            }

        }

        if (settings?.redirectOnCompletion && !settings.redirectURL) {
            setError("Redirect URL required");
            return;
        }

        if (settings?.passwordProtected && !settings.passwordPlain) {
            setError("Password required");
            return;
        }

        if (settings?.notifyEmails) {

            
            const emails = settings?.notifyEmails?.split(",");

            console.log("[FormSettings/handleSave] notifyEmails: ", settings.notifyEmails);
            console.log("[FormSettings/handleSave] emails: ", emails);

            for (let i=0; i<emails.length; i++) {
                if (!validateEmail(emails[i].trim())) {
                    setError("One or more invalid emails (separate with comma only)");
                    return;
                }
            }

            setError("");
        }

        if (showSubmissionLimit && settings.submissionLimit === null) {
            setError("Submission limit required");
            return;
        }

        if (showSubmissionLimit && settings.submissionLimit !== null && settings.submissionLimit < 1) {
            setError("Submission limit must be greater than 0");
            return;
        }

        setError("");

        const payload : UpdateFormSettingsPayload = {
            ...settings,
            formId: editingForm.id,
        }

        console.log("[FormSettings/handleSave] payload: ", payload)

        const updatedForm = await apiUpdateFormSettings(payload);

        if (!updatedForm) {
            toast("Can not update settings", toastFail)
            return;
        }

        console.log("[FormSettings/handleSave] updatedForm: ", updatedForm)

        toast("Settings updated", toastSuccess);

        await apiFormHasEdits(updatedForm.id);
        updatedForm.hasEdits = true;

        dispatch(setEditingForm({...updatedForm}));
        // dispatch(setUIStates({...uiStates, viewingBuild: true, viewingSettings: false}));
        // navigate("/editor");
    }

    const handleChangeTimezone = (value: string) => {
        console.log("[FormSettings/handleChangeTimezone] value: ", value);

        const timezone = TIMEZONES.find((timezone: Timezone) => {
            return timezone.id === value
        })
            
        if (!settings || !timezone) {
            return;
        }

        const offset = timezone.gmtOffset;

        const date = settings?.closeDate || new Date();
        const newDate = createDateWithOffset(
            date.getFullYear(), date.getMonth() + 1, date.getDate(), 
            hours, minutes,
            offset)

        setSettings({
            ...settings,
            closeDateTimezone: offset,
            closeDateTimezoneId: timezone.id,
            closeDate: newDate,
        } as FormSettings)

    }

    const handleChangeTimezoneOld = (e: any) => {
        console.log("[FormSettings/handleChangeTimezone] e: ", e.target.value);

        const timezone = TIMEZONES.find((timezone: Timezone) => {
            return timezone.id === e.target.value
        })
            
        if (!settings || !timezone) {
            return;
        }

        const offset = timezone.gmtOffset;

        const date = settings?.closeDate || new Date();
        const newDate = createDateWithOffset(
            date.getFullYear(), date.getMonth() + 1, date.getDate(), 
            hours, minutes,
            offset)

        setSettings({
            ...settings,
            closeDateTimezone: offset,
            closeDateTimezoneId: timezone.id,
            closeDate: newDate,
        } as FormSettings)

    }

    const handleChangeHours = (value: string) => {
        console.log("[FormSettings/handleChangeHours] value: ",value);

        const newHours = parseInt(value);

        setHours(newHours);

        const date = settings?.closeDate ? new Date(settings.closeDate) : new Date();
        const newDate = createDateWithOffset(
            date.getFullYear(), date.getMonth() + 1, date.getDate(), 
            newHours, minutes,
            settings?.closeDateTimezone || -8)

        setSettings({
            ...settings,
            closeDate: newDate,
        } as FormSettings)

    }

    const handleChangeHoursOld = (e: any) => {
        console.log("[FormSettings/handleChangeHours] e: ", e.target.value);

        const newHours = parseInt(e.target.value);

        setHours(newHours);

        const date = settings?.closeDate ? new Date(settings.closeDate) : new Date();
        const newDate = createDateWithOffset(
            date.getFullYear(), date.getMonth() + 1, date.getDate(), 
            newHours, minutes,
            settings?.closeDateTimezone || -8)

        setSettings({
            ...settings,
            closeDate: newDate,
        } as FormSettings)

    }

    const handleChangeMinutes = (value: string) => {
        console.log("[FormSettings/handleChangeMinutes] value: ", value);

        const newMinutes = parseInt(value);

        setMinutes(newMinutes);

        const date = settings?.closeDate || new Date();
        const newDate = createDateWithOffset(
            date.getFullYear(), date.getMonth() + 1, date.getDate(), 
            hours, newMinutes,
            settings?.closeDateTimezone || -8)

        setSettings({
            ...settings,
            closeDate: newDate,
        } as FormSettings)
    }

    const handleChangeMinutesOld = (e: any) => {
        console.log("[FormSettings/handleChangeMinutes] e: ", e.target.value);

        const newMinutes = parseInt(e.target.value);
        setMinutes(newMinutes);

        const date = settings?.closeDate || new Date();
        const newDate = createDateWithOffset(
            date.getFullYear(), date.getMonth() + 1, date.getDate(), 
            hours, newMinutes,
            settings?.closeDateTimezone || -8)

        setSettings({
            ...settings,
            closeDate: newDate,
        } as FormSettings)
    }

    const handleSwitchSettings = (name: string, checked: any) => {
        if (!settings) {
            return;
        }

        switch (name) {
            case "closed":
            case "redirectOnCompletion":
            case "notifyCreator":
            case "notifyResponder":
            case "progressBar":
            case "questionNumbers":
            case "passwordProtected":
                setSettings({...settings, [name]: checked})
                break;
            case "closeOnDate":
                let newSettings : FormSettings = {...settings}

                if (checked && !settings.closeDate) {
                    newSettings.closeDate = new Date();
                } else if (!checked) {
                    newSettings.closeDate = null;
                }
                console.error("Updated date settings: ", newSettings)
                setSettings(newSettings)

                break;
            default:
                // do nothing
                console.log("Should not be here")
        }
    }

    const handleDiameter = 16;
    const width = 36;
    const height = 20;

    // console.log("[FormSettings] settings date: ", settings?.closeDate);
    // console.log("[FormSettings] settings timezone: ", settings?.closeDateTimezone);


    return (
        <div id="form-settings">
            <div className="form-settings-header">
                <FormHeader />
            </div>
            {settings &&
                <div className="form-settings-body">

                    <div className="form-settings-body-title">
                        {/* Form Settings */}
                    </div>

                    <div className="form-settings-wrapper">

                        <div className="form-settings-main">
                            <div className="form-settings-menu">
                                <div className="form-settings-section-header">
                                    <div 
                                        className={"form-settings-menu-item " + (menu === SETTINGS_MENU.GENERAL ? "form-settings-menu-item-selected" : "")} 
                                        onClick={() => setMenu(SETTINGS_MENU.GENERAL)}
                                    >
                                        General
                                    </div>
                                </div>
                                
                                <div className="form-settings-section-header">
                                    <div 
                                        className={"form-settings-menu-item " + (menu === SETTINGS_MENU.ACCESS ? "form-settings-menu-item-selected" : "")} 
                                        onClick={() => setMenu(SETTINGS_MENU.ACCESS)}
                                    >
                                        Access
                                    </div>
                                </div>

                                <div className="form-settings-section-header">
                                    <div 
                                        className={"form-settings-menu-item " + (menu === SETTINGS_MENU.COMPLETION ? "form-settings-menu-item-selected" : "")} 
                                        onClick={() => setMenu(SETTINGS_MENU.COMPLETION)}
                                    >
                                        Completion
                                    </div>
                                </div>
                                {/* <div className="form-settings-section-header">
                                    <div 
                                        className={"form-settings-menu-item " + (menu === SETTINGS_MENU.NOTIFICATIONS ? "form-settings-menu-item-selected" : "")} 
                                        onClick={() => setMenu(SETTINGS_MENU.NOTIFICATIONS)}
                                    >
                                        Notifications
                                    </div>
                                </div> */}
                            </div>
                        

                            <div className="form-settings-container">
                                {menu === SETTINGS_MENU.GENERAL &&
                                    <>
                                        <div className="form-settings-label">
                                            Look and Feel
                                        </div>
                                        <div className="form-settings-section">
                                            <div className="form-settings-type-toggle">
                                                <div className="form-settings-type-toggle-left">
                                                    Progress Bar
                                                </div>
                                                <div className="form-settings-type-toggle-right">
                                                    <MySwitch
                                                        checked={settings.progressBar}
                                                        onChange={(checked: boolean) => 
                                                            handleSwitchSettings("progressBar", checked)} 
                                                    />
                                                    
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-settings-section">
                                            <div className="form-settings-type-toggle">
                                                <div className="form-settings-type-toggle-left">
                                                    Question Numbers
                                                </div>
                                                <div className="form-settings-type-toggle-right">
                                                    <MySwitch
                                                        checked={settings.questionNumbers}
                                                        onChange={(checked: boolean) => 
                                                            handleSwitchSettings("questionNumbers", checked)} 
                                                    />
                                                
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                }

                                {/* Access */}
                                {menu === SETTINGS_MENU.ACCESS &&
                                    <>
                                        <div className="form-settings-label">
                                            Accessing Your Form
                                        </div>
                                        <div className="form-settings-section">
                                            <div className="form-settings-type-toggle">
                                                <div className="form-settings-type-toggle-left">
                                                    Form is Closed
                                                </div>
                                                <div className="form-settings-type-toggle-right">
                                                    <MySwitch
                                                        checked={settings.closed}
                                                        onChange={(checked: boolean) => handleSwitchSettings("closed", checked)} 
                                                    />

                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-settings-section">
                                            <div className="form-settings-type-toggle">
                                                <div className="form-settings-type-toggle-left">
                                                    Close on a Date
                                                </div>
                                                <div className="form-settings-type-toggle-right">
                                                    <MySwitch
                                                        checked={!!settings.closeDate} 
                                                        onChange={(checked: boolean) => handleSwitchSettings("closeOnDate", checked)} 
                                                    />
                                                    
                                                </div>
                                            </div>

                                            {settings.closeDate &&
                                                <>
                                                    <div className="form-settings-type-multiple">
                                                        
                                                        <div className="form-settings-close-timezone">
                                                            Timezone:&nbsp;
                                                            <GenericSelect
                                                                options={TIMEZONES.map((timezone: Timezone) => {
                                                                    return {
                                                                        value: timezone.id,
                                                                        displayName: timezone.timezone,
                                                                    }
                                                                })}
                                                                selected={{ 
                                                                    value: settings.closeDateTimezoneId || "US/Pacific", 
                                                                    displayName: settings.closeDateTimezoneId || "US/Pacific" 
                                                                }}
                                                                onChange={(timezone) => {
                                                                    handleChangeTimezone(timezone)
                                                                }}
                                                                isFont={false}
                                                            />
                                                            
                                                            {/* <select
                                                                value={settings.closeDateTimezoneId || "US/Pacific"}
                                                                onChange={handleChangeTimezoneOld}
                                                            >
                                                                {TIMEZONES.map((timezone: Timezone, index: number) => {   
                                                                    return (
                                                                        <option 
                                                                            key={"timezone-" + index + "-" + timezone.gmtOffset}
                                                                            value={timezone.id}
                                                                            selected={settings.closeDateTimezone === timezone.gmtOffset}
                                                                            onChange={handleChangeSetting}
                                                                        >
                                                                            {timezone.timezone}
                                                                        </option>
                                                                    )

                                                                })}
                                                            </select> */}
                                                        </div>

                                                        <div className="form-settings-close-date">
                                                            Date:&nbsp;
                                                            <DatePicker 
                                                                id="effono-datepicker"
                                                                selected={pickerDate} 
                                                                onChange={(date) => handleChangeCloseDate(date)} 
                                                            />
                                                        </div>
                                                
                                                        <div className="form-settings-close-time">

                                                            <div className="form-settings-subvalue-label">
                                                                Time (24 Hour):&nbsp;
                                                            </div>
                                                            
                                                            <GenericSelect
                                                                options={HOURS.map((hour: number) => {
                                                                    return {
                                                                        value: hour,
                                                                        displayName: hour.toString().padStart(2, '0'),
                                                                    }
                                                                })}
                                                                selected={{ 
                                                                    value: hours,
                                                                    displayName: hours.toString().padStart(2, '0') 
                                                                }}
                                                                onChange={(hour) => {
                                                                    handleChangeHours(hour)
                                                                }}
                                                                isFont={false}
                                                            />

                                                            {/* <select 
                                                                value={hours}
                                                                onChange={handleChangeHoursOld}
                                                            >
                                                                {HOURS.map((hour: number) => {
                                                                    return (
                                                                        <option 
                                                                            key={"hour-" + hour}
                                                                            value={hour}
                                                                        >
                                                                            {hour.toString().padStart(2, '0')}
                                                                        </option>
                                                                    )
                                                                })}
                                                            </select> */}

                                                            <GenericSelect
                                                                options={MINUTES.map((minute: number) => {
                                                                    return {
                                                                        value: minute,
                                                                        displayName: minute.toString().padStart(2, '0'),
                                                                    }
                                                                })}
                                                                selected={{ 
                                                                    value: minutes,
                                                                    displayName: minutes.toString().padStart(2, '0') 
                                                                }}
                                                                onChange={(minute) => {
                                                                    handleChangeMinutes(minute)
                                                                }}
                                                                isFont={false}
                                                            />

                                                            {/* <select 
                                                                value={minutes}
                                                                onChange={handleChangeMinutesOld}
                                                            >
                                                                {MINUTES.map((minute: number) => {
                                                                    return (
                                                                        <option 
                                                                            key={"minute-" + minute}
                                                                            value={minute}
                                                                        >
                                                                            {minute.toString().padStart(2, '0')}
                                                                        </option>
                                                                    )
                                                                })}
                                                            </select> */}
                                                        </div>
                                                    </div>  
                                                    
                                                </>
                                            }
                                        </div>

                                        <div className="form-settings-section">
                                            <div className="form-settings-type-toggle">
                                                <div className="form-settings-type-toggle-left">
                                                    Submission Limit
                                                </div>
                                                <div className="form-settings-type-toggle-right">
                                                    <MySwitch
                                                        checked={showSubmissionLimit}
                                                        onChange={(checked: boolean) => {
                                                            setSettings({...settings, submissionLimit: null})
                                                            setShowSubmissionLimit(checked)
                                                        }}
                                                    />
                                                
                                                </div>
                                                
                                            </div>
                                            
                                            {showSubmissionLimit &&
                                                <div className="form-settings-subvalue">
                                                    <div className="form-settings-subvalue-label">
                                                        Max Submissions:
                                                    </div>
                                                    <div className="form-settings-type-value">
                                                        <input 
                                                            type="number"
                                                            name="submissionLimit"
                                                            value={settings.submissionLimit || ""}
                                                            onChange={handleChangeSetting}
                                                        />
                                                    </div>  
                                                </div>
                                                
                                            }
                                            
                                        </div>

                                        
                                
                                        <div className="form-settings-section">
                                            <div className="form-settings-type-toggle">
                                                <div className="form-settings-type-toggle-left">
                                                    Require Password
                                                </div>
                                                <div className="form-settings-type-toggle-right">
                                                    <MySwitch
                                                        checked={settings.passwordProtected}
                                                        onChange={(checked: boolean) => 
                                                            handleSwitchSettings("passwordProtected", checked)} 
                                                    />
                                                
                                                </div>
                                            </div>

                                            {/* Hide this because of weird password value reset issue */}
                                            <div 
                                                className="form-settings-subvalue"
                                                style={{display: settings.passwordProtected ? "flex" : "none"}}
                                            >
                                                <div className="form-settings-subvalue-label">
                                                    Password:
                                                </div>
                                                <div 
                                                    className="form-settings-type-value"
                                                >
                                                    <input 
                                                        name="passwordPlain"
                                                        type="password"
                                                        value={settings?.passwordPlain}
                                                        onChange={handleChangeSetting}
                                                    />
                                                </div>  
                                            </div>
                                        </div>
                                    </>
                                }
                                    
                                {menu === SETTINGS_MENU.COMPLETION &&
                                    <>
                                        <div className="form-settings-label">
                                            On Completion
                                        </div>

                                        {/* Redirect */}
                                        {/* <div className="form-settings-section-header">
                                            Redirect on Completion
                                        </div> */}

                                        <div className="form-settings-section">
                                            <div className="form-settings-type-toggle">
                                                <div className="form-settings-type-toggle-left">
                                                    Redirect on Completion
                                                </div>
                                                <div className="form-settings-type-toggle-right">
                                                    <MySwitch
                                                        checked={settings.redirectOnCompletion}
                                                        onChange={(checked: boolean) => 
                                                            handleSwitchSettings("redirectOnCompletion", checked)} 
                                                    />
                                                
                                                </div>
                                            </div>

                                            <div 
                                                className="form-settings-subvalue"
                                                style={{
                                                    display: settings.redirectOnCompletion ? "flex" : "none",
                                                    width: "100%"
                                                }}
                                            >

                                                <div 
                                                    className="form-settings-type-value"
                                                    style={{width: "100%"}}
                                                >
                                                    <input 
                                                        type="text"
                                                        name="redirectURL"
                                                        placeholder="https://"
                                                        value={settings.redirectURL}
                                                        onChange={handleChangeSetting}
                                                        width="100%"
                                                    />
                                                </div>  
                                            </div>
                                        </div>

                                        <div className="form-settings-section">
                                            <div className="form-settings-type-toggle">
                                                <div className="form-settings-type-toggle-left">
                                                    Notifications
                                                </div>
                                                <div className="form-settings-type-toggle-right">
                                                    <MySwitch
                                                        checked={settings.notifyCreator}
                                                        onChange={(checked: boolean) => 
                                                            handleSwitchSettings("notifyCreator", checked)} 
                                                    />
                                                    
                                                </div>
                                            </div>

                                            <div 
                                                className="form-settings-subvalue"
                                                style={{
                                                    display: settings.notifyCreator ? "flex" : "none",
                                                    width: "100%"
                                                }}
                                            >
                                                <div 
                                                    className="form-settings-type-value"
                                                    style={{width: "100%"}}
                                                >
                                                    <input 
                                                        type="text"
                                                        name="notifyEmails"
                                                        value={settings?.notifyEmails}
                                                        onChange={handleChangeSetting}
                                                        width="100%"
                                                        placeholder='Emails (separate with comma)'
                                                    />
                                                </div>  
                                            </div>
                                        </div> 
                                    </>
                                }
                            </div>
                        </div>
                        <div className="form-settings-footer">
                            <>
                                {/* Error */}
                                {error && 
                                    <div className="form-settings-error">
                                        {error}
                                    </div>
                                }


                                {/* Actions */}
                                <div className="form-settings-actions">
                                    <div className="modal-buttons">
                                        <button
                                            className="button-save"
                                            onClick={handleSave}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div> 
                            </>
                        </div>
                    </div>
                    
                    
                </div>
            }

            <Toaster />

        </div>
    )
}